import PropTypes from 'prop-types';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { APPLICATION_BODY_ID } from '../../constants/settings.constants';

const Flyout = ({ rootElement, parentElement, relative, children, size = 'full', direction = 'down', isOpen }) => {
  const contentRef = useRef(null);
  const [top, setTop] = useState(0);
  const [width, setWidth] = useState(0);

  const rootElementBottom = rootElement?.getBoundingClientRect()?.bottom ?? 0;
  const rootElementWidth = rootElement?.getBoundingClientRect()?.width ?? 0;
  const applicationLayoutElement = document.getElementById('js-spo-application-layout');

  useEffect(() => {
    let isMounted = true;
    if (!isMounted || size !== 'full') {
      return;
    }
    setWidth(rootElementWidth);

    return () => {
      isMounted = false;
    };
  }, [size, rootElementWidth]);

  useEffect(() => {
    let isMounted = true;
    if (!isMounted) {
      return;
    }

    setTop(rootElementBottom);

    return () => {
      isMounted = false;
    };
  }, [rootElementBottom]);

  const onScroll = useCallback((rootElement) => {
    if (!rootElement) {
      return;
    }
    setTop(rootElement.getBoundingClientRect().bottom);
  }, []);

  const onParentScroll = useCallback((rootElement, parentElement) => {
    if (!rootElement || !parentElement) {
      return;
    }
    setTop(rootElement.getBoundingClientRect().bottom);
  }, []);

  useLayoutEffect(() => {
    let isMounted = true;
    const scrollListener = () => {
      if (isMounted) {
        onScroll(rootElement);
      }
    };

    if (isMounted) {
      document.getElementById(APPLICATION_BODY_ID).addEventListener('scroll', scrollListener);
    }

    return () => {
      isMounted = false;
      window.removeEventListener('scroll', scrollListener);
    };
  }, [rootElement, onScroll]);

  useLayoutEffect(() => {
    let isMounted = true;
    const scrollListener = () => {
      if (isMounted) {
        onParentScroll(rootElement, parentElement);
        onParentScroll(rootElement, applicationLayoutElement);
      }
    };

    if (isMounted && parentElement) {
      parentElement.addEventListener('scroll', scrollListener);
    }

    if (isMounted && applicationLayoutElement) {
      applicationLayoutElement.addEventListener('scroll', scrollListener);
    }

    return () => {
      isMounted = false;
      if (parentElement) {
        parentElement.removeEventListener('scroll', scrollListener);
      }
      if (isMounted && applicationLayoutElement) {
        applicationLayoutElement.addEventListener('scroll', scrollListener);
      }
    };
  }, [applicationLayoutElement, parentElement, rootElement, onParentScroll]);

  useLayoutEffect(() => {
    if (!rootElement) {
      return;
    }
    setTop(rootElement.getBoundingClientRect().bottom);
  }, [rootElement, parentElement?.offsetHeight, parentElement?.scrollHeight]);

  const getTranslate = () => {
    if (isOpen && direction === 'up') {
      return { transform: `translateY(${-48 - contentRef.current?.offsetHeight ?? 0}px)` };
    }
    return {};
  };

  if (!rootElement) {
    return null;
  }

  return (
    <div
      className={`spo-m-flyout m-flyout m-flyout--${size} spo-m-flyout--${direction} ${isOpen ? 'is-open' : ''} ${
        relative ? 'relative' : ''
      }`}
    >
      <div
        ref={contentRef}
        style={{
          ...(size === 'full'
            ? {
                width,
                ...(width ? { minWidth: width, maxWidth: width } : {})
              }
            : {}),
          ...(relative ? {} : { top }),
          ...getTranslate()
        }}
        className="spo-m-flyout__content m-flyout__content"
      >
        {children}
      </div>
    </div>
  );
};

Flyout.propTypes = {
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'full'])
};
export default Flyout;
